import React from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Collapse } from "antd";
import { ErrorBoundary } from "@appsignal/react";
import ReactHtmlParser from "react-html-parser";
import Checkbox from "@appComponents/Checkbox/Checkbox";
import TermsAndConditionsAnnouncementsService from "@services/TermsAndCconditionsAnnouncements.services";
import { classNames } from "@features/utils/reactFunctions";
import { AgreementsConfig } from "@bundles/consts";
import { TermsTypes } from "@appInterfaces/agreements";

import { appsignal } from "./../../appsignal";
import * as styles from "./AcceptAndConfirmListAppStyle.module.scss";
import * as termsStyles from "../TermsAndConditionsApp/style.module.scss";
import * as elements from "@appStyles/_elements.module.scss";

type Props = {
  data: Announcement[];
  previewMode?: boolean;
};

type Announcement = {
  id: number;
  header: string;
  description: string;
  full_content: string;
  type: TermsTypes;
};

const validationSchema = () =>
  Yup.object({
    agreement_0: Yup.mixed().test(
      "agreement-check",
      "All required.",
      (_, context) =>
        Object.values(context.parent).every((agreement) => agreement)
    ),
  });

export const AcceptAndConfirmListApp = ({
  data,
  previewMode = false,
}: Props) => {
  const initialValues = data.reduce(
    (result, _, index) => ({
      ...result,
      [`agreement_${index}`]: false,
    }),
    { agreement_0: false }
  );

  const formik = useFormik<{ [index: string]: number | boolean }>({
    initialValues,
    onSubmit: async (values) => {
      TermsAndConditionsAnnouncementsService.create({
        ids: Object.values(values) as number[],
        return_url: (new URLSearchParams(window.location.search)).get('return_url'),
      })
        .then((res) => (window.location.href = res.data.url))
        .catch((e) => {
          formik.resetForm();
          formik.validateForm();
          (window as any).toastr.error(
            "There was an unexpected error. Please try again. Our development team was notified."
          );
          throw new Error(`TermsAndConditionsAnnouncementsService error: ${e}`);
        });
    },
    validationSchema: validationSchema(),
    validateOnMount: true,
  });

  return (
    <ErrorBoundary instance={appsignal}>
      <form onSubmit={formik.handleSubmit} className={styles.form}>
        {data.map((announcement, index) => (
          <div key={announcement.header} className={styles.formRow}>
            <p className={styles.formRowHeader}>{announcement.header}</p>
            <div className={styles.formRowContent}>
              <Checkbox
                {...formik.getFieldProps(`agreement_${index}`)}
                onChange={(e) => {
                  formik.setFieldValue(
                    `agreement_${index}`,
                    e.currentTarget.checked ? announcement.id : false
                  );
                }}
                checked={Boolean(formik.values[`agreement_${index}`])}
              />
              <div>{ReactHtmlParser(announcement.description)}</div>
            </div>
            <hr />
          </div>
        ))}
        <Collapse>
          {data.map((data) => {
            if (data.full_content) {
              return (
                <Collapse.Panel
                  header={`View ${
                    AgreementsConfig[data.type].section_name
                  } Terms & Conditions`}
                  key={data.id}
                >
                  <div
                    className={classNames(
                      termsStyles.termsWrapper,
                      styles.termsWrapper
                    )}
                  >
                    {ReactHtmlParser(data.full_content)}
                  </div>
                </Collapse.Panel>
              );
            } else {
              return null;
            }
          })}
        </Collapse>
        {!previewMode && (
          <div className={styles.formSubmitWrapper}>
            <button
              type="submit"
              className={`${elements.btn} ${elements.btnSuccess} `}
              disabled={!formik.isValid}
            >
              I agree to the new terms and conditions
            </button>
          </div>
        )}
      </form>
    </ErrorBoundary>
  );
};

export default (props: Props) => <AcceptAndConfirmListApp {...props} />;
